import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from '@/api/client';
import type {
  ClientWire,
  ClientListResponseWire,
  ClientStatisticsWire,
  ClientTypeWire,
  LoyaltyTierWire,
} from '@efacture/core';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────
//
// The response shapes are the shared contracts from @efacture/core
// (docs/ARCHITECTURE.md, "API↔web response contracts"), aliased under
// this module's names.

/** Code of the client-type table (DGI CC/PM/PP/PC plus the tenant's rows, §2.35). */
export type ClientType = ClientTypeWire;
export type LoyaltyTier = LoyaltyTierWire;
export type Client = ClientWire;
export type ClientListResponse = ClientListResponseWire;

export interface ClientListParams {
  page?: number | undefined;
  limit?: number | undefined;
  search?: string | undefined;
  type?: ClientType | undefined;
  includeInactive?: boolean | undefined;
  /** Clients whose type requires an IFU and whose IFU a local invoice refuses. */
  ifuNonConforme?: boolean | undefined;
}

export interface CreateClientInput {
  type: ClientType;
  name?: string | undefined;
  firstName?: string | undefined;
  ifu?: string | undefined;
  rccm?: string | undefined;
  phone?: string | undefined;
  email?: string | undefined;
  address?: string | undefined;
  code?: string | undefined;
  notes?: string | undefined;
  tags?: string[] | undefined;
}

export interface UpdateClientInput {
  type?: ClientType | undefined;
  name?: string | undefined;
  firstName?: string | undefined;
  ifu?: string | undefined;
  rccm?: string | undefined;
  phone?: string | undefined;
  email?: string | undefined;
  address?: string | undefined;
  code?: string | undefined;
  notes?: string | undefined;
  tags?: string[] | undefined;
  isActive?: boolean | undefined;
}

export interface AdjustLoyaltyPointsInput {
  points: number;
  description: string;
}

export type ClientStatistics = ClientStatisticsWire;

// ─────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────

export const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: (params: ClientListParams) => [...clientKeys.lists(), params] as const,
  details: () => [...clientKeys.all, 'detail'] as const,
  detail: (id: string) => [...clientKeys.details(), id] as const,
  statistics: () => [...clientKeys.all, 'statistics'] as const,
};

// ─────────────────────────────────────────────────────────────
// API Functions
// ─────────────────────────────────────────────────────────────

async function fetchClients(params: ClientListParams): Promise<ClientListResponse> {
  return apiClient.get<ClientListResponse>('/clients', {
    page: params.page,
    limit: params.limit,
    search: params.search,
    type: params.type,
    includeInactive: params.includeInactive,
    ifuNonConforme: params.ifuNonConforme || undefined,
  });
}

async function fetchClient(id: string): Promise<Client> {
  return apiClient.get<Client>(`/clients/${id}`);
}

async function createClient(input: CreateClientInput): Promise<Client> {
  return apiClient.post<Client>('/clients', input);
}

async function updateClient(id: string, input: UpdateClientInput): Promise<Client> {
  return apiClient.put<Client>(`/clients/${id}`, input);
}

async function deleteClient(id: string): Promise<void> {
  return apiClient.delete<void>(`/clients/${id}`);
}

async function adjustLoyaltyPoints(id: string, input: AdjustLoyaltyPointsInput): Promise<Client> {
  return apiClient.post<Client>(`/clients/${id}/loyalty/adjust`, input);
}

async function fetchClientStatistics(): Promise<ClientStatistics> {
  return apiClient.get<ClientStatistics>('/clients/statistics');
}

// ─────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────

/**
 * Hook to fetch a paginated list of clients
 * GET /api/clients
 */
export function useClients(params: ClientListParams = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: clientKeys.list(params),
    queryFn: () => fetchClients(params),
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch a single client by ID
 * GET /api/clients/:id
 */
export function useClient(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: clientKeys.detail(id),
    queryFn: () => fetchClient(id),
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: (options?.enabled ?? true) && !!id,
  });
}

// ─────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────

/** Refusal of an IFU another client of the company carries (409); its message names that client. */
export function isClientIfuDuplicate(error: unknown): error is ApiError {
  return error instanceof ApiError && error.code === 'CLIENT_IFU_DUPLICATE';
}

/**
 * Hook to create a new client
 * POST /api/clients
 */
export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateClientInput) => createClient(input),
    onSuccess: () => {
      // Invalidate all client lists to refresh data
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      // Invalidate statistics as they may have changed
      queryClient.invalidateQueries({ queryKey: clientKeys.statistics() });
    },
  });
}

/**
 * Hook to update an existing client
 * PUT /api/clients/:id
 */
export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateClientInput }) =>
      updateClient(id, input),
    onSuccess: (updatedClient) => {
      // Update the specific client in cache
      queryClient.setQueryData(clientKeys.detail(updatedClient.id), updatedClient);
      // Invalidate lists to reflect changes
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      // Invalidate statistics if active status changed
      queryClient.invalidateQueries({ queryKey: clientKeys.statistics() });
    },
  });
}

/**
 * Hook to soft delete a client
 * DELETE /api/clients/:id
 */
export function useDeleteClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteClient(id),
    onSuccess: (_, id) => {
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: clientKeys.detail(id) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      // Invalidate statistics
      queryClient.invalidateQueries({ queryKey: clientKeys.statistics() });
    },
  });
}

/**
 * Hook to adjust client loyalty points
 * POST /api/clients/:id/loyalty/adjust
 */
export function useAdjustLoyaltyPoints() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AdjustLoyaltyPointsInput }) =>
      adjustLoyaltyPoints(id, input),
    onSuccess: (updatedClient) => {
      // Update the specific client in cache
      queryClient.setQueryData(clientKeys.detail(updatedClient.id), updatedClient);
      // Invalidate lists to reflect point changes
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Utility Exports
// ─────────────────────────────────────────────────────────────

export {
  fetchClients,
  fetchClient,
  createClient,
  updateClient,
  deleteClient,
  adjustLoyaltyPoints,
  fetchClientStatistics,
};
